import { CaretDownOutlined, ReloadOutlined } from '@ant-design/icons';
import Button from '@ui-library/button';
import DestinationIcon from '@components/common/icons/destinationIcon';
import SourceIcon from '@components/common/icons/sourceIcon';
import theme from '@css/theme';
import useStores from '@stores/useStores';
import Svg from '@svg/index';
import { Select } from 'antd';
import _ from 'lodash';
import * as React from 'react';
import { Header, SubHeader } from '@components/sources/source/warehouseSource/configureData/style';
import exportResponseError from '@utils/exportResponseError';
import { FixMe } from '@utils/types';
import { IColumn } from '@components/sources/source/warehouseSource/configureData/sourceSpecificConfig';
import { isArrayEqual, useConnectionState, IField } from '../../visualDataMappingProvider';
import classes from './mapping-commons.module.scss';
import { ColumnView, TableSelect } from './mappingView/mappingViewHelpers';
import { getColumnDetails, getFieldDetails } from './mappingView';
import RefreshButton from './refreshButton';
import { fetchErrorAssets } from '../../errorsView';

const { Option } = Select;

export interface RefreshButtonTextProps {
  type: 'text';
  refreshing: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
}

export const reloadButton = (buttonProperties: RefreshButtonTextProps, type?: string) => {
  const { refreshing, onClick } = buttonProperties;
  const buttonText = `${refreshing ? 'Fetching' : 'Reload'} ${type}`;
  return (
    <Button
      textPrimary
      className={`${classes.reload_button}`}
      disabled={refreshing}
      onClick={onClick}
    >
      <ReloadOutlined className={classes.reload_icon} spin={refreshing} />
      &nbsp;
      {type && (
        <span className={refreshing ? classes.text_on_reload : classes.text}>{buttonText}</span>
      )}
    </Button>
  );
};

export const refreshFieldsSetup = (
  hide: boolean,
  buttonComp: FixMe,
  setParentDisabled?: (disableParent: boolean) => void,
) => {
  const { messagesStore, workspaceStore } = useStores();
  const { fields, setFields, destination, destinationConfig, updateSchemaErrors } =
    useConnectionState();
  const timeout = 35000;
  const onCompleteRefresh = (fieldsConfig: { fields: IField[] }) => {
    const [fieldsToCreate, oldFields] = _.partition(fields, { createdNow: true });

    const newFields = !isArrayEqual(oldFields, fieldsConfig.fields);
    if (newFields) {
      setFields([...fieldsConfig.fields, ...fieldsToCreate]);
    }
  };
  return (
    <RefreshButton
      hide={hide}
      onComplete={onCompleteRefresh}
      onIncomplete={(err) => {
        const errMsg = exportResponseError(err)?.message;
        if (errMsg) {
          updateSchemaErrors([fetchErrorAssets('fields', errMsg)]);
          messagesStore?.showErrorMessage(errMsg);
        }
      }}
      setParentDisabled={setParentDisabled}
      field={{
        url: `integrationsInfo/${destination?.destinationDefinition?.name}/fields`,
        method: 'post',
        timeout,
        params: {
          config: {
            destinationId: destination?.id,
            workspaceId: workspaceStore.id,
            ...destination.config,
          },
          ...destinationConfig,
        },
      }}
      ButtonComp={buttonComp}
    />
  );
};

export const refreshColumnsSetup = (
  hide: boolean,
  buttonComp: FixMe,
  setParentDisabled?: (disableParent: boolean) => void,
) => {
  const { messagesStore, sourceDataStore } = useStores();
  const { columns, setColumns, sourceConfig, source, updateSchemaErrors, modelAccountId } =
    useConnectionState();
  const onCompleteRefresh = (newColumns: IColumn[]) => {
    const isNewColumns = !isArrayEqual(newColumns, columns);
    if (isNewColumns) {
      setColumns(newColumns);
    }
  };
  const sourceDefName = source.sourceDef?.name || '';
  const accountId = source.config?.accountId || modelAccountId || '';

  const fetchColumns = async () => {
    updateSchemaErrors([fetchErrorAssets('columns')]);

    const columnsDetails: IColumn[] =
      (await sourceDataStore.getColumns(
        {
          accountId,
          ...sourceConfig.table,
          table: sourceConfig.table?.name,
          sqlModelId: sourceConfig.sqlModelId,
        },
        sourceDefName,
      )) || [];
    return columnsDetails;
  };
  return (
    <RefreshButton
      hide={hide}
      onComplete={onCompleteRefresh}
      onIncomplete={(err) => {
        const errMsg = exportResponseError(err)?.message;
        if (errMsg) {
          messagesStore?.showErrorMessage(errMsg);
          updateSchemaErrors([fetchErrorAssets('columns', errMsg)]);
        }
      }}
      setParentDisabled={setParentDisabled}
      fetchDataOnClick={fetchColumns}
      ButtonComp={buttonComp}
    />
  );
};

const SelectUniqueIdentifier = ({ setLoading }: { setLoading: (isLoading: boolean) => void }) => {
  const {
    fields,
    columns,
    source,
    destination,
    fieldMap,
    viewOnly,
    ifUpdate,
    setUniqueIdentifierConfig,
    uniqueIdentifierConfig,
    columnMap,
    updateSchemaErrors,
  } = useConnectionState();
  const uniqueFields = fields.filter((field) => field.isUnique);

  const [disabled, setDisabled] = React.useState({ mapped: false, original: false });
  const sourceDefName = source.sourceDef?.name || '';

  const config = uniqueIdentifierConfig;

  if (fields.length === 0 || columns.length === 0) {
    return null;
  }

  const fieldRefreshButtonGenerator = (props: RefreshButtonTextProps) => (
    <div className={classes.dropdown_reload_button_wrapper}>{reloadButton(props, 'fields')}</div>
  );

  const disableUniqFieldParent = (val: boolean) => {
    if (val) {
      updateSchemaErrors([fetchErrorAssets('fields')]);
    }
    setDisabled({ ...disabled, mapped: val });
  };

  const colRefreshButtonGenerator = (props: RefreshButtonTextProps) => (
    <div className={classes.dropdown_reload_button_wrapper}>{reloadButton(props, 'columns')}</div>
  );

  const disableUniqColParent = (val: boolean) => {
    setDisabled({ ...disabled, original: val });
  };
  return (
    <div className={classes.select_uniq_container}>
      <div className={classes.select_uniq_header_wrapper}>
        <Header>Choose identifier</Header>
        <SubHeader>Which columns should we use to identify your records</SubHeader>
      </div>

      <div className={classes.select_uniq_body}>
        <div className={classes.wh_columns_container}>
          <div className={`${classes.vdmTable_header} ${classes.uniq_header}`}>
            <SourceIcon source={sourceDefName} size={theme.iconSize.medium} />
            <div className={classes.header}>Warehouse columns</div>
          </div>

          <TableSelect
            value={config.original}
            optionFilterProp="label"
            filterOption
            hasError={config.original && !columnMap[config.original]}
            disabled={disabled.original || viewOnly || ifUpdate}
            showSearch
            placeholder="Search or add column"
            defaultValue={config.original}
            suffixIcon={<CaretDownOutlined />}
            onChange={(val) => {
              setUniqueIdentifierConfig({ original: val as string });
              setLoading(!uniqueIdentifierConfig.mapped);
            }}
          >
            {columns.map((col: IColumn) => (
              <Option
                value={col.name}
                label={col.name}
                key={`single-select-opt-key-${col.name}-${col.name}`}
              >
                <ColumnView column={getColumnDetails(columnMap, col.name)} />
              </Option>
            ))}
          </TableSelect>
          {refreshColumnsSetup(
            Boolean(viewOnly || ifUpdate),
            colRefreshButtonGenerator,
            disableUniqColParent,
          )}
        </div>

        <div className={classes.middle_arrow_container}>
          <Svg
            fill="#2E3A59"
            height={12}
            width={16}
            className={classes.mapping_arraw_icon}
            name="right-arrow"
          />

          {!(viewOnly || ifUpdate) && <div className={classes.footer} />}
        </div>
        <div className={classes.dest_fields_container}>
          <div className={`${classes.vdmTable_header} ${classes.uniq_header}`}>
            <DestinationIcon destination={destination?.destinationDefinition?.name} />
            <div className={classes.header}>Destination fields</div>
          </div>
          <TableSelect
            value={config.mapped}
            optionFilterProp="label"
            filterOption
            hasError={config.mapped && !fieldMap[config.mapped]}
            disabled={disabled.mapped || viewOnly || ifUpdate || uniqueFields.length === 0}
            showSearch
            placeholder="Search or add field"
            suffixIcon={<CaretDownOutlined />}
            onChange={(val) => {
              setUniqueIdentifierConfig({ mapped: val as string });
              setLoading(!uniqueIdentifierConfig.original);
            }}
          >
            {uniqueFields.map((field) => (
              <Option
                value={field.name}
                label={field.label}
                key={`single-select-opt-key-${field.name}-${field.label}`}
              >
                <ColumnView column={getFieldDetails(fieldMap, field.name)} />
              </Option>
            ))}
          </TableSelect>
          {refreshFieldsSetup(
            Boolean(viewOnly || ifUpdate),
            fieldRefreshButtonGenerator,
            disableUniqFieldParent,
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectUniqueIdentifier;
